/* eslint-disable react/jsx-props-no-spreading */
import { useDropzone } from 'react-dropzone';
import type { VoidFunctionComponent } from 'react';

import { archiveUploadAccept, validArchiveUploadExtensions } from '../../../utils';
import Icon, { icons } from '../Icon';
import { ICON_SIZE } from '../Icon/icon-constants';

import { FormLabelVariant } from './FormLabel';
import LabeledTextInput from './LabeledTextInput';

import styles from './UploadFile.module.less';

export type Props = {
  /**
   * File selected by the user, if any.
   */
  file?: File;
  /**
   * Callback to set the file selected by the user.
   */
  setFile: (file: File | undefined) => void;

  /**
   * Optional URL value.
   */
  url?: string;
  /**
   * Optional callback to set the URL input by the user. If provided,
   * a URL input field will be shown in addition to the file upload.
   */
  setURL?: (url: string) => void;

  /**
   * Whether the upload and URL inputs should be disabled (e.g. while the file is being uploaded).
   */
  isDisabled?: boolean;
};

/**
 * Component for uploading a file with an optional URL input. The URL input
 * will only be shown when the `setURL` callback is provided.
 */
const UploadFile: VoidFunctionComponent<Props> = ({
  file,
  setFile,
  url,
  setURL,
  isDisabled,
}) => {
  const onDropFile = (acceptedFiles: File[]) => {
    const acceptedFile = acceptedFiles[0];
    if (!acceptedFile) {
      return;
    }

    setFile(acceptedFile);
    setURL?.('');
  };

  const onChangeURL = (value: string) => {
    setURL?.(value);
    setFile(undefined);
  };

  // Use react-dropzone via the hook API:
  // https://react-dropzone.js.org/
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: onDropFile,
    disabled: isDisabled,
    accept: archiveUploadAccept,
  });

  // Only show the "OR URL" option if the setURL callback is provided.
  const shouldShowURL = !!setURL;

  return (
    <div className={styles.uploadFile}>
      {/* When a file is selected, show the file name and a remove button. */}
      {file ? (
        <LabeledTextInput
          type="text"
          label="Local archive"
          labelVariant={FormLabelVariant.Filter}
          value={file.name}
          suffix={
            <button type="button" className={styles.removeFileButton} onClick={() => setFile(undefined)}>
              <Icon icon={icons.trashRegular} size={ICON_SIZE.LG} />
            </button>
          }
          isDisabled
        />
      ) : (
        <>
          {/* When no file is selected, show the dropzone and optional URL input. */}
          <p className={styles.dropzoneLabel}>Local archive</p>
          <div className={styles.dropzoneRoot} {...getRootProps()}>
            <input {...getInputProps()} />
            <Icon icon={icons.upload} size={ICON_SIZE.XXL} />
            <p className={styles.instructions}>Drop your files here or <u>Browse files</u></p>
          </div>
          {shouldShowURL && (
            <>
              <p className={styles.orSeparator}>OR</p>
              <LabeledTextInput
                size="small"
                label="URL-based archive"
                labelVariant={FormLabelVariant.Filter}
                placeholder="https://dependency.com/code.zip"
                value={url}
                onChange={onChangeURL}
              />
            </>
          )}
          <p className={styles.extensions}><b>Supported extensions:</b> {validArchiveUploadExtensions.join(', ')}</p>
        </>
      )}
    </div>
  );
};

export default UploadFile;
